import React from 'react';

/* ---------------------------------------------------------------
   "What's coming next for your restaurant" — roadmap section.
   Features grouped into two lanes by status (In beta / Coming soon).
   No external dependencies beyond React.
--------------------------------------------------------------- */

const COLORS = {
  navy: '#2A2A66',
  navySoft: '#6B6B93',
  muted: '#6B7280',
  borderLight: '#E4E2ED',
  orange: '#F4791F',
  indigo: '#5B4FE0',
};

const iconProps = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const RoadmapIcon = {
  Weather: () => (
    <svg {...iconProps}>
      <path d='M17.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.4-1.5A4 4 0 0 0 6.5 16' />
      <circle cx='6' cy='6' r='2' />
      <line x1='6' y1='1' x2='6' y2='2.4' />
      <line x1='1' y1='6' x2='2.4' y2='6' />
      <line x1='2.3' y1='2.3' x2='3.3' y2='3.3' />
    </svg>
  ),
  Roi: () => (
    <svg {...iconProps}>
      <polyline points='3 17 9 11 13 15 21 7' />
      <polyline points='14 7 21 7 21 14' />
    </svg>
  ),
  Bundle: () => (
    <svg {...iconProps}>
      <path d='M21 8l-9-5-9 5 9 5 9-5z' />
      <path d='M3 8v8l9 5 9-5V8' />
      <line x1='12' y1='13' x2='12' y2='21' />
    </svg>
  ),
  Forecast: () => (
    <svg {...iconProps}>
      <line x1='3' y1='21' x2='21' y2='21' />
      <polyline points='3 16 8 11 12 14 15 10' />
      <polyline points='15 10 18 7 21 4' strokeDasharray='2 3' />
      <circle cx='15' cy='10' r='1.5' />
    </svg>
  ),
  Reviews: () => (
    <svg {...iconProps}>
      <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
      <polygon points='12 6.5 13.1 8.8 15.5 9.1 13.7 10.7 14.2 13 12 11.8 9.8 13 10.3 10.7 8.5 9.1 10.9 8.8 12 6.5' />
    </svg>
  ),
  Compare: () => (
    <svg {...iconProps}>
      <path d='M8 3v14a2 2 0 0 0 2 2h10' />
      <path d='M16 21V7a2 2 0 0 0-2-2H4' />
    </svg>
  ),
};

const LANES = [
  {
    key: 'beta',
    label: 'In beta now',
    note: 'Live with a group of partner restaurants',
    color: COLORS.indigo,
    tint: 'rgba(91,79,224,0.08)',
    items: [
      {
        Icon: RoadmapIcon.Roi,
        title: 'True ROI reporting',
        desc: 'Most platforms count every order that touched a promo. We separate the orders a promo actually created, so you know what your promo spend really earns.',
      },
      {
        Icon: RoadmapIcon.Compare,
        title: 'See where you stand nearby',
        desc: "Anonymized benchmarks from restaurants around you: what they charge for delivery and which promos they're running. Other delivery apps don't show you this.",
      },
      {
        Icon: RoadmapIcon.Reviews,
        title: 'Shared review window',
        desc: 'Customer reviews land in one shared window with your store, so you see feedback as it comes in and can respond directly. Spot what customers love and fix issues before they cost you orders.',
      },
    ],
  },
  {
    key: 'soon',
    label: 'Coming soon',
    note: 'In development, shipping next',
    color: COLORS.orange,
    tint: 'rgba(244,121,31,0.08)',
    items: [
      {
        Icon: RoadmapIcon.Weather,
        title: 'Weather & event-driven promos',
        desc: 'We watch the forecast and local events and tell you what to promote: a soup special before a cold snap, a delivery deal during the game down the street.',
      },
      {
        Icon: RoadmapIcon.Bundle,
        title: 'Smart bundle suggestions',
        desc: 'Combos built from what your customers already order together, priced to protect your margin. No spreadsheets on your end.',
      },
      {
        Icon: RoadmapIcon.Forecast,
        title: 'AI sales forecasting',
        desc: "Know how much you're likely to sell before the day starts. Our AI looks at your order history, the day of the week, weather, and local events to predict sales, so you can staff and prep with confidence.",
      },
    ],
  },
];

function Lane({ lane }) {
  return (
    <div
      style={{
        flex: '1 1 380px',
        minWidth: 0,
        background: '#fff',
        border: `1px solid ${COLORS.borderLight}`,
        borderTop: `4px solid ${lane.color}`,
        borderRadius: 16,
        overflow: 'hidden',
      }}
    >
      {/* lane header */}
      <div
        style={{
          padding: '18px 24px',
          background: lane.tint,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: lane.color,
              display: 'inline-block',
            }}
          />
          <span style={{ fontSize: 15, fontWeight: 700, color: lane.color }}>{lane.label}</span>
        </div>
        <span style={{ fontSize: 12.5, color: COLORS.muted }}>{lane.note}</span>
      </div>

      {/* features */}
      {lane.items.map(({ Icon, title, desc }, i) => (
        <div
          key={title}
          style={{
            display: 'flex',
            gap: 16,
            padding: '22px 24px',
            borderTop: i === 0 ? 'none' : `1px solid ${COLORS.borderLight}`,
          }}
        >
          <div
            style={{
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: 12,
              background: lane.tint,
              color: lane.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon />
          </div>
          <div>
            <h4
              style={{
                margin: '0 0 6px',
                fontSize: 16.5,
                fontWeight: 700,
                color: COLORS.navy,
                lineHeight: 1.3,
              }}
            >
              {title}
            </h4>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: COLORS.muted }}>{desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function RestaurantRoadmap() {
  return (
    <section
      style={{
        padding: '72px 24px',
        fontFamily: "'Inter', system-ui, sans-serif",
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        .roadmap-cta:focus-visible { outline: 3px solid rgba(91,79,224,0.4); outline-offset: 3px; }
        .roadmap-cta:hover { background: #4A3FD0; }
      `}</style>

      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        {/* header */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h2
            style={{
              fontWeight: 800,
              fontSize: 'clamp(28px, 4vw, 36px)',
              color: COLORS.navy,
              margin: '0 0 12px',
              letterSpacing: '-0.01em',
            }}
          >
            What's coming next for your restaurant
          </h2>
          <p
            style={{
              fontSize: 16,
              lineHeight: 1.6,
              color: COLORS.navySoft,
              maxWidth: 580,
              margin: '0 auto',
            }}
          >
            We're building these alongside restaurants like yours. Here's what's in beta today and
            what's shipping next.
          </p>
        </div>

        {/* lanes */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
          {LANES.map((lane) => (
            <Lane key={lane.key} lane={lane} />
          ))}
        </div>

        {/* CTA */}
      </div>
    </section>
  );
}
